import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import debounce from 'lodash/debounce';

// Common Components Import
import ProductTableSales from '../../../Common/SalesMProduct';
import ChargesSection from "../../../Common/ChargesSection";
import { CHARGE_OPTIONS, UNIT_OPTIONS } from "../../../Common/constants";
import CommonSalesOrderFields from '../CommonSalesOrderFields';

import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';

function CreatePumpDomesticSO() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [sono, setsono] = useState('');
  const [sodate, setsodate] = useState('');
  const [quotationno, setquotationno] = useState('');
  const [quotationdate, setquotationdate] = useState('');
  const [amendmentno, setAmendmentno] = useState('');
  const [amendmentdate, setAmendmentdate] = useState('');
  const [customerpono, setCustomerpono] = useState('');
  const [customerpodate, setCustomerpodate] = useState('');
  const [ldclause, setLdclause] = useState('');
  const [customermaterialcode, setcustomermaterialcode] = useState('');
  const [deliveryterm, setdeliveryterm] = useState('');
  const [soValidationMsg, setsoValidationMsg] = useState('');
  const [issoValid, setIssoValid] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Common fields data state
  const [commonFieldsData, setCommonFieldsData] = useState({
    customer: null,
    customerDetails: {
      contactperson: '',
      contactno: '',
      email: '',
      freight: '',
      packingandforwarding: '',
      paymentterm: ''
    },
    customerMultipleAddress: null,
    branch: null,
    warehouse: null
  });

  // Product and charges state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
  const [rows, setRows] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([
    {
      id: Date.now(),
      description: "",
      chargepercent: 0,
      amount: 0,
      gstPercent: 0,
      isPercentBased: true,
    },
  ]);

  // Handle common fields data change
  const handleCommonFormData = (data) => {
    setCommonFieldsData(data);
  };

  // Validate sales order number
  const checksono = async (number) => {
    if (!number) {
      setsoValidationMsg('');
      setIssoValid(false);
      return;
    }

    if (!/^[A-Za-z0-9-]+$/.test(number)) {
      setsoValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
      setIssoValid(false);
      return;
    }

    try {
      const response = await axios.get(`${BASE_URL}salesOrder/check/${number}`, {
        headers: authHeader(),
      });

      if (response.data === true) {
        setsoValidationMsg("❌ This sales order number already exists.");
        setIssoValid(false);
      } else {
        setsoValidationMsg("✅ This sales order number is available.");
        setIssoValid(true);
      }
    } catch (error) {
      setsoValidationMsg("❌ Error validating sales order number.");
      setIssoValid(false);
    }
  };

  useEffect(() => {
    const debouncedCheck = debounce(() => {
      checksono(sono);
    }, 400);

    debouncedCheck();

    return () => debouncedCheck.cancel();
  }, [sono]);

  // Fetch product data
  const getPumpProductData = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
        { headers: authHeader() }
      );
      const products = response.data.Index || response.data.data || [];
      setProductOptions(
        products.map((product) => ({
          value: product.id,
          label: `${product.description} - ${product.moc} - ${product.size} - ${product.tagnumber} - ${product.serialnumber} - ${product.partno}`,
          description: product.description,
          uom: product.unitofmeasurement || UNIT_OPTIONS[0].value,
          igst: product.igst || 0,
          partno: product.partno,
          serialnumber: product.serialnumber,
          modelnumber: product.modelnumber,
          tagnumber: product.tagnumber,
          hsnsacno: product.hsnsacno,
          moc: product.moc || "",
          type: product.type || "",
          size: product.size || "",
          rating: product.rating || "",
          classes: product.classes || "",
          schedule: product.schedule || "",
          certification: product.certification || "",
        }))
      );
    } catch (error) {
      Swal.fire("Error", "Failed to fetch product data", "error");
    }
  };

  useEffect(() => {
    getPumpProductData();
  }, [productSearch]);

  const handleProductSearchChange = debounce((inputValue) => {
    setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
  }, 500);

  // Handle product selection
  const handleProductChange = (selectedOption) => {
    setSelectedProduct(selectedOption);
    const selectedProductData = productOptions.find(
      (product) => product.value === selectedOption.value
    );

    if (selectedProductData) {
      const newRow = {
        description: selectedProductData.description,
        value: selectedProductData.value,
        label: selectedProductData.label,
        productremarks: "",
        quantity: "",
        uom: selectedProductData.uom || "",
        unitRateInr: selectedProductData.unitrateinr || 0,
        gstPercent: selectedProductData.igst || 0,
        amount: selectedProductData.unitrateinr || 0,
        totalinr: (selectedProductData.unitrateinr || 0) * (1 + (selectedProductData.igst || 0) / 100),
        hsnsacno: selectedProductData.hsnsacno,
        partno: selectedProductData.partno,
        serialnumber: selectedProductData.serialnumber,
        moc: selectedProductData.moc || "",
        type: selectedProductData.type || "",
        size: selectedProductData.size || "",
        rating: selectedProductData.rating || "",
        classes: selectedProductData.classes || "",
        schedule: selectedProductData.schedule || "",
        certification: selectedProductData.certification || "",
      };
      setRows([...rows, newRow]);
    }
  };

  // Create empty charge row
  const createEmptyCharge = () => ({
    id: Date.now(),
    description: "",
    chargepercent: 0,
    amount: 0,
    gstPercent: 0,
    isPercentBased: true,
  });

  // Handle charge changes with 2 decimal places
  const handleChargeChange = (index, field, value, isBlur = false) => {
    setAdditionalCharges(prev => {
      const newCharges = [...prev];
      const charge = { ...newCharges[index] };
      const netTotal = rows.reduce((sum, row) => sum + (row.totalinr || 0), 0);

      if (field === "chargepercent") {
        const percentage = Math.min(value, 100);
        charge.chargepercent = parseFloat(percentage.toFixed(2));

        if (isBlur || percentage === 100) {
          const calculatedAmount = (percentage * netTotal) / 100;
          charge.amount = parseFloat(calculatedAmount.toFixed(2));
        } else {
          charge.amount = 0;
        }
      }
      else if (field === "amount") {
        charge.amount = parseFloat(value.toFixed(2));
        charge.chargepercent = 0;
      }
      else if (field === "gstPercent") {
        charge.gstPercent = parseFloat(value.toFixed(2));
      }
      else {
        charge[field] = value;
      }

      newCharges[index] = charge;
      return newCharges;
    });
  };

  // Add new charge row
  const addNewChargeRow = () => {
    setAdditionalCharges((prev) => [...prev, createEmptyCharge()]);
  };

  // Remove charge row
  const removeChargeRow = (index) => {
    if (additionalCharges.length > 1) {
      setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Remove product row
  const removeRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  // Handle row changes with 2 decimal places
  const handleRowChange = (index, key, value) => {
    const updatedRows = [...rows];
    updatedRows[index][key] = value;

    if (["quantity", "unitRateInr", "gstPercent"].includes(key)) {
      const quantity = parseFloat(updatedRows[index].quantity) || 0;
      const unitRateInr = parseFloat(updatedRows[index].unitRateInr) || 0;
      const gstPercent = parseFloat(updatedRows[index].gstPercent) || 0;

      const amount = parseFloat((quantity * unitRateInr).toFixed(2));
      const gstAmount = parseFloat(((amount * gstPercent) / 100).toFixed(2));
      const totalinr = parseFloat((amount + gstAmount).toFixed(2));

      updatedRows[index].amount = amount;
      updatedRows[index].totalinr = totalinr;
    }

    setRows(updatedRows);
  };

  // Calculate products total with 2 decimal places
  const productsTotal = useMemo(() => {
    const total = rows.reduce((total, row) => {
      const rowTotal = (row.quantity || 0) * (row.unitRateInr || 0) * (1 + (row.gstPercent || 0) / 100);
      return total + rowTotal;
    }, 0);
    return parseFloat(total.toFixed(2));
  }, [rows]);

  useEffect(() => {
    // Recalculate charge amounts based on percentages when productsTotal changes
    setAdditionalCharges(prevCharges => {
      return prevCharges.map(charge => {
        if (charge.chargepercent > 0) {
          return {
            ...charge,
            amount: parseFloat(((charge.chargepercent * productsTotal) / 100).toFixed(2))
          };
        }
        return charge;
      });
    });
  }, [productsTotal]);

  // Calculate charges total with 2 decimal places
  const additionalChargesTotal = useMemo(() => {
    const total = additionalCharges.reduce((total, charge) => {
      const chargeTotal = (charge.amount || 0) * (1 + (charge.gstPercent || 0) / 100);
      return total + chargeTotal;
    }, 0);
    return parseFloat(total.toFixed(2));
  }, [additionalCharges]);

  // Calculate gross total and round to nearest integer with 2 decimal places
  const grossTotal = parseFloat((productsTotal + additionalChargesTotal).toFixed(2));
  const roundedGrossTotal = Math.round(grossTotal);
  const grandTotal = roundedGrossTotal;

  // Form validation
  const validateForm = () => {
    if (!issoValid) {
      Swal.fire("Error", "Please enter a valid sales order number", "error");
      return false;
    }
    if (!commonFieldsData.customer) {
      Swal.fire("Error", "Please select a customer", "error");
      return false;
    }
    if (!commonFieldsData.customerMultipleAddress) {
      Swal.fire("Error", "Please select customer address", "error");
      return false;
    }
    if (rows.length === 0) {
      Swal.fire("Error", "Please add at least one product", "error");
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    // Prepare sales order items with 2 decimal places
    const salesOrderItems = rows.map((row) => ({
      product: { id: row.value },
      quantity: row.quantity,
      uom: row.uom,
      unitrateinr: parseFloat(row.unitRateInr.toFixed(2)),
      gst: parseFloat(row.gstPercent.toFixed(2)),
      amount: parseFloat(row.amount.toFixed(2)),
      totalinr: parseFloat(row.totalinr.toFixed(2)),
      productremarks: row.productremarks,
      partno: row.partno || "",
      hsnsacno: row.hsnsacno || "",
      serialnumber: row.serialnumber || "",
      moc: row.moc || "",
      type: row.type || "",
      size: row.size || "",
      rating: row.rating || "",
      classes: row.classes || "",
      schedule: row.schedule || "",
      certification: row.certification || "",
    }));

    // Prepare additional charges with 2 decimal places
    const additionalChargesData = additionalCharges
      .filter((charge) => charge.description && (charge.amount > 0 || charge.chargepercent > 0))
      .map((charge) => {
        const chargeAmount = parseFloat(charge.amount.toFixed(2));
        const gstPercent = parseFloat(charge.gstPercent.toFixed(2));
        const gstAmount = parseFloat((chargeAmount * gstPercent / 100).toFixed(2));
        const chargetotal = parseFloat((chargeAmount + gstAmount).toFixed(2));

        return {
          chargetype: charge.description,
          chargepercent: parseFloat(charge.chargepercent.toFixed(2)),
          chargeamount: chargeAmount,
          chargegst: gstPercent,
          chargetotal: chargetotal,
          isPercentBased: charge.isPercentBased,
        };
      });

    // Calculate totals with 2 decimal places
    const additionalChargesTotalCalc = parseFloat(additionalChargesData.reduce(
      (sum, charge) => sum + charge.chargetotal,
      0
    ).toFixed(2));

    const productsTotalCalc = parseFloat(salesOrderItems.reduce(
      (sum, item) => sum + item.totalinr,
      0
    ).toFixed(2));

    const grossTotalCalc = parseFloat((productsTotalCalc + additionalChargesTotalCalc).toFixed(2));

    // Prepare payload
    const payload = {
      customer: commonFieldsData.customer ? { id: commonFieldsData.customer.value } : null,
      contactperson: commonFieldsData.customerDetails?.contactperson,
      contactno: commonFieldsData.customerDetails?.contactno,
      email: commonFieldsData.customerDetails?.email,
      customeraddress: commonFieldsData.customerMultipleAddress
        ? { id: commonFieldsData.customerMultipleAddress.value }
        : null,
      freight: commonFieldsData.customerDetails.freight,
      packingandforwarding: commonFieldsData.customerDetails.packingandforwarding,
      paymentterm: commonFieldsData.customerDetails.paymentterm,
      branch: commonFieldsData.branch ? { id: commonFieldsData.branch.value } : null,
      warehouse: commonFieldsData.warehouse ? { id: commonFieldsData.warehouse.value } : null,
      salesOrderItems: salesOrderItems,
      additionalCharges: additionalChargesData.length > 0 ? additionalChargesData : null,
      nettotalproduct: productsTotalCalc,
      additionalchargestotal: additionalChargesTotalCalc,
      grosstotal: grossTotalCalc,
      grandtotalproduct: grossTotalCalc,
      salesordertype: "Manufacturing",
      salesordercategory: "Domestic",
      sodate,
      sono,
      customermaterialcode,
      ldclause,
      deliveryterm,
      quotationno,
      quotationdate,
      amendmentno,
      amendmentdate,
      customerpono,
      customerpodate,
    };

    try {
      const response = await axios.post(`${BASE_URL}salesOrder/save`, payload, {
        headers: authHeader(),
      });
      Swal.fire("Success", "Sales order created successfully", "success");
      navigate("/salesOrderIndexTable/1");
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message ||
        error.response?.data ||
        "Failed to create sales order",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Sales Order Header Information */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Sales Order Number: *</Form.Label>
              <Form.Control
                type="text"
                value={sono}
                onChange={e => {
                  setsono(e.target.value);
                  setFormSubmitted(false);
                }}
                isInvalid={
                  (formSubmitted && !issoValid) ||
                  soValidationMsg.includes('exists') ||
                  soValidationMsg.includes('Error') ||
                  soValidationMsg.includes('allowed')
                }
                required
              />
              {soValidationMsg && (
                <Form.Control.Feedback type={
                  soValidationMsg.includes('exists') ||
                    soValidationMsg.includes('Error') ||
                    soValidationMsg.includes('allowed') ?
                    "invalid" : "valid"
                }>
                  {soValidationMsg}
                </Form.Control.Feedback>
              )}
              {formSubmitted && !issoValid && !soValidationMsg && (
                <Form.Control.Feedback type="invalid">
                  Please enter a valid sales order number
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Sales Order Date</Form.Label>
              <Form.Control
                type="date"
                value={sodate}
                onChange={(e) => setsodate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="sono">
              <Form.Label>Quotation No.:</Form.Label>
              <Form.Control
                type="text"
                value={quotationno}
                onChange={(e) => setquotationno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="sono">
              <Form.Label>Quotation Date</Form.Label>
              <Form.Control
                type="date"
                value={quotationdate}
                onChange={(e) => setquotationdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="amendmentno">
              <Form.Label>Amendment No.</Form.Label>
              <Form.Control
                type="text"
                value={amendmentno}
                onChange={(e) => setAmendmentno(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="amendmentdate">
              <Form.Label>Amendment Date</Form.Label>
              <Form.Control
                type="date"
                value={amendmentdate}
                onChange={(e) => setAmendmentdate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="customerpono">
              <Form.Label>Customer PO No.</Form.Label>
              <Form.Control
                type="text"
                value={customerpono}
                onChange={(e) => setCustomerpono(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="customerpodate">
              <Form.Label>Customer PO Date</Form.Label>
              <Form.Control
                type="date"
                value={customerpodate}
                onChange={(e) => setCustomerpodate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="ldclause">
              <Form.Label>LD Clause</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={ldclause}
                onChange={(e) => setLdclause(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Common Sales Order Fields */}
        <CommonSalesOrderFields
          onDataChange={handleCommonFormData}
          initialData={commonFieldsData}
        />

        {/* Sales Order Details */}
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="sono">
              <Form.Label>Customer Material code</Form.Label>
              <Form.Control
                type="text"
                value={customermaterialcode}
                onChange={(e) => setcustomermaterialcode(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="sono">
              <Form.Label>Delivery Term</Form.Label>
              <Form.Control
                type="text"
                value={deliveryterm}
                onChange={(e) => setdeliveryterm(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Products Section */}
        <div className="mt-4">
          <h5>Products</h5>
          <Row>
            <Col md="12">
              <Form.Group>
                <Form.Label>Select Product</Form.Label>
                <Select
                  value={selectedProduct}
                  onChange={handleProductChange}
                  options={productOptions}
                  placeholder="Search and select product"
                  onInputChange={handleProductSearchChange}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Common ProductTable Component */}
          <ProductTableSales
            rows={rows}
            unitOptions={UNIT_OPTIONS}
            onRowChange={handleRowChange}
            onRemoveRow={removeRow}
          />
        </div>

        {/* Charges Section */}
        <ChargesSection
          additionalCharges={additionalCharges}
          chargeOptions={CHARGE_OPTIONS}
          productsTotal={productsTotal}
          additionalChargesTotal={additionalChargesTotal}
          grossTotal={grossTotal}
          grandTotal={grandTotal}
          onChargeChange={handleChargeChange}
          onAddCharge={addNewChargeRow}
          onRemoveCharge={removeChargeRow}
        />

        {/* Submit Buttons */}
        <div className="d-flex justify-content-end mt-4">
          <button
            type="submit"
            className="btn btn-primary me-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                <span className="ms-2">Submitting...</span>
              </>
            ) : (
              "Submit"
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
        </div>
      </Form>
    </div>
  );
}

export default CreatePumpDomesticSO;